/**
 * HttpStatus codes.
 */
export const HttpStatus = {
  200: 'OK',
  201: 'Created',
  202: 'Accepted',
  203: 'Non-Authoritative Information',
  204: 'No Content',
  205: 'Reset Content',
  206: 'Partial Content',
  300: 'Multiple Choices',
  301: 'Moved Permanently',
  302: 'Found',
  303: 'See Other',
  304: 'Not Modified',
  305: 'Use Proxy',
  306: 'Unused',
  307: 'Temporary Redirect',
  400: 'Bad Request',
  401: 'Unauthorized',
  402: 'Payment Required',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  406: 'Not Acceptable',
  407: 'Proxy Authentication Required',
  408: 'Request Timeout',
  409: 'Conflict',
  410: 'Gone',
  411: 'Length Required',
  412: 'Precondition Required',
  413: 'Request Entry Too Large',
  414: 'Request-URI Too Long',
  415: 'Unsupported Media Type',
  416: 'Requested Range Not Satisfiable',
  417: 'Expectation Failed',
  418: "I'm a teapot",
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  501: 'Not Implemented',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
  505: 'HTTP Version Not Supported',
};

/**
 * To parse the field errors on component.
 * @param {string} field Name of the field on component
 * @param {Array<string>} errorList list of error for field.
 * @returns Html formatted error message for field.
 */
function formatFieldError(field, errorList) {
  let rows = '';
  for (let i = 1; i < errorList.length; i++) {
    rows += ` <tr>
                <td></td>
                <td></td>
                <td>&nbsp;${errorList[i]}</td>
              </tr>`;
  }
  return `<br/>
    <table>
      <tbody>
        <tr>
          <td>&nbsp;&nbsp;&nbsp;&nbsp;</td>
          <td><b>${field}</b>: </td>
          <td>&nbsp;${errorList[0]}</td>
        </tr>
        ${rows}
      </tbody>
    </table>`;
}

/**
 * To format validation errors ignoring field categories.
 * @param {Object} errors errors object
 * @returns Html formatted error message.
 */
function formatValidationErrors(errors) {
  let items = '';
  Object.keys(errors).forEach((field) => {
    for (let i = 0; i < errors[field].length; i++) {
      items += `<div>${errors[field][i]}</div>`;
    }
  });
  return items;
}

/**
 * Defines a problem detail returned from server as part of exception.
 * To define problem detail returned from server.
 * @param {string} instance instance on server side where error occured
 * @param {string} detail detailed message of problem
 */
export function ProblemDetail({
  instance, detail, title, errors
}) {
  this.name = 'ProblemDetail';
  this.instance = instance;
  this.title = title;
  this.detail = detail;
  this.message = detail;

  /**
   * list of errors is returned when a DomainException is returned from server.
   * Problem detail structure is different for a DomainException.
   */
  if (typeof errors === 'object') {
    this.validationErrors = errors;
    this.message = formatValidationErrors(errors);
    this.detail = ''; // initilaize with empty string.
    Object.keys(errors).forEach((field) => {
      // format errors in html format for detail.
      this.detail += formatFieldError(field, errors[field]);
    });
  }

  /**
   * To write problem message to console.
   * @returns {void}
   */
  this.writeToConsole = () => console.error(this);

  /**
   * To get string message of the problem.
   * @returns {string} Message of problem.
   */
  this.getDetail = () => this?.detail;

  /**
   * To get string message of the problem.
   * @returns {string} Message of problem.
   */
  this.getMessage = () => this?.message;
}

/**
 * Defines an api error returned from server.
 * @param {int} statusCode status code
 * @param {string} statusText status message
 * @param {ProblemDetail} problemDetail error message
 */
export function ApiError(
  statusCode,
  statusText,
  problemDetail = new ProblemDetail()
) {
  this.name = 'ApiError';
  this.statusCode = statusCode;
  this.statusText = statusText;
  this.problemDetail = problemDetail;

  /**
   * To write error message to console.
   * @returns {void}
   */
  this.writeToConsole = () => console.error(this);

  /**
   * To get the string message of problem.
   * @returns {string} Message of problem returned form server.
   */
  this.getProblemDetail = () => this.problemDetail?.getDetail();

  /**
   * To get the string message of problem.
   * @returns {string} Message of problem returned form server.
   */
  this.getProblemMessage = () => this.problemDetail?.getMessage();

  /**
   * To get the formatted string message of the api error.
   * @returns {string} Formatted string message of api error with status code and problem message.
   */
  this.getFormattedMessage = () => `${this.statusText}=${this.statusCode}: ${this.problemDetail?.getDetail()}`;
}

/**
 * Make ApiError an Error so that try...catch can recognize.
 */
ApiError.prototype = Object.create(Error.prototype);

/**
 *
 * @param {ApiError} ex Error trapped in try...catch.
 * @returns {string} Error message.
 */
export const formatError = (ex) => {
  /**
   * A typical exception trapped in try...catch when using crV2Fetch defined in baseQuery.js
   */
  if (ex?.name === ApiError.name) {
    return ex?.getProblemDetail();
  }
  if (ex?.error?.name === ApiError.name) {
    /**
     * An exception that gets trapped in crV2BaseQuery
     */
    return ex.error?.getProblemDetail();
  }
  return HandleGenericErrors(ex);
};

/**
 *
 * @param {ApiError} ex Error trapped in try...catch.
 * @returns {string} Error message.
 */
export const getErrorMessage = (ex) => {
  /**
   * A typical exception trapped in try...catch when using crV2Fetch defined in baseQuery.js
   */
  if (ex?.name === ApiError.name) {
    return ex?.getProblemMessage();
  }
  if (ex?.error?.name === ApiError.name) {
    /**
     * An exception that gets trapped in crV2BaseQuery
     */
    return ex.error?.getProblemMessage();
  }
  return HandleGenericErrors(ex);
};

function HandleGenericErrors(ex) {
  if (ex?.error?.data) {
    const data = ex?.error?.data;
    const { status } = data;

    const apiError = new ApiError(
      status,
      HttpStatus[status],
      new ProblemDetail(data)
    );
    return apiError?.getProblemDetail();
  }

  /**
   * Default cases like network error with return message in exception.
   */
  return ex?.message;
}
